import { useEffect, useRef, useState } from "react"

/**
 * 计算最大可用宽度
 * @param windowWidth 窗口宽度
 * @returns 最大可用宽度（px）
 */
function getMaxAvailableWidth(windowWidth: number): number {
  if (windowWidth < 750) {
    // H5: 100vw - 86px
    return windowWidth - 86
  } else if (windowWidth <= 1200) {
    // 小于等于 1200px: 100vw - 64px
    return windowWidth - 64
  } else if (windowWidth <= 1380) {
    // 大于 1200px 且小于等于 1380px: 1200 - 64 = 1136px
    return 1200 - 64
  } else {
    // 大于 1440px: 1380 - 64 = 1316px
    return 1380 - 64
  }
}

/**
 * 文本溢出检测 Hook
 * @description 检测文本是否超过容器最大可用宽度，决定是否需要滚动
 * @param text 要检测的文本内容
 * @returns 是否需要滚动，以及用于测量的 ref
 */
export function useTextOverflow(text: string | undefined) {
  const [shouldScroll, setShouldScroll] = useState(false)
  const measureRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (typeof window === "undefined" || !measureRef.current || !text) {
      setShouldScroll(false)
      return
    }

    const measureTextWidth = () => {
      if (!measureRef.current) {
        return
      }

      const windowWidth = window.innerWidth
      const maxAvailableWidth = getMaxAvailableWidth(windowWidth)
      const textWidth = measureRef.current.offsetWidth

      setShouldScroll(textWidth > maxAvailableWidth)
    }

    // 使用 ResizeObserver 监听测量元素的大小变化
    const resizeObserver = new ResizeObserver(() => {
      measureTextWidth()
    })

    resizeObserver.observe(measureRef.current)

    // 初始测量（延迟一帧确保 DOM 已渲染并应用样式）
    const timeoutId = setTimeout(() => {
      measureTextWidth()
    }, 0)

    // 监听窗口 resize 事件
    let ticking = false
    const handleResize = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          measureTextWidth()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener("resize", handleResize, { passive: true })

    return () => {
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
      window.removeEventListener("resize", handleResize)
    }
  }, [text])

  return { shouldScroll, measureRef }
}
